// ============================================================
// Analytics central — Google Analytics 4 (gtag) + Meta Pixel
// ============================================================
//
// - pageView()           → GA4 page_view + Meta PageView (con dedup)
// - trackEvent()         → GA4 event (category, action, label)
// - trackWhatsApp()      → GA4 + Meta "Contact" cuando abren WhatsApp
// - trackCTA()           → atajo para botones "Agendar llamada"
// - trackFunnelStep()    → pasos del formulario
// - trackLead()          → "generate_lead" en GA4 + "Lead" en Meta
// - getUtmParams()      → arma el objeto con todos los UTMs + fbclid
//
// GA4 ID: G-XJS0GQJH78 (configurado en index.html)
// GA ID viejo: G-R3E8B4KDQN (legacy, react-ga4) — se mantiene por compat
// ============================================================

import ReactGA from "react-ga4";

// GA4 legacy init (react-ga4) — se mantiene para no romper tracking viejo
if (typeof window !== "undefined") {
  ReactGA.initialize("G-R3E8B4KDQN");
}

export const GA_ID = "G-XJS0GQJH78";

const generateEventId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ---------- gtag wrapper ----------
function gtag(...args: unknown[]) {
  if (
    typeof window !== "undefined" &&
    typeof (window as any).gtag === "function"
  ) {
    (window as any).gtag(...args);
  }
}

// ---------- UTM helpers ----------
export type UtmParams = {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  fbclid?: string | null;
  a?: string | null; // ángulo (distribuidores, etc.)
  url_origen?: string | null;
};

// Lee los UTMs del localStorage (guardados en App.tsx al primer ingreso)
export function getUtmParams(): UtmParams {
  if (typeof window === "undefined") return {};
  return {
    utm_source: localStorage.getItem("utm_source") || "",
    utm_medium: localStorage.getItem("utm_medium") || "",
    utm_campaign: localStorage.getItem("utm_campaign") || "",
    utm_content: localStorage.getItem("utm_content") || "",
    utm_term: localStorage.getItem("utm_term") || "",
    fbclid: localStorage.getItem("fbclid") || "",
    a: localStorage.getItem("a") || "",
    url_origen: localStorage.getItem("url_origen") || window.location.href,
  };
}

// ---------- Page view ----------
export function pageView(path?: string, title?: string) {
  const pagePath = path || window.location.pathname + window.location.search;
  const pageTitle = title || document.title;

  // GA4 (gtag) — page_view manual
  gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: pagePath,
    page_title: pageTitle,
  });

  // GA4 legacy (react-ga4)
  try {
    ReactGA.send({ hitType: "pageview", page: pagePath });
  } catch (e) {
    /* noop */
  }
}

// ---------- Generic event ----------
export function trackEvent(
  category: string,
  action: string,
  label?: string,
  value?: number,
  extraParams?: Record<string, unknown>,
) {
  // GA4 (gtag) — usa action como event name, category como event_category
  gtag("event", action, {
    event_category: category,
    event_label: label,
    value,
    ...extraParams,
    ...getUtmParams(),
  });

  // GA4 legacy (react-ga4)
  try {
    ReactGA.event({ category, action, label, value });
  } catch (e) {
    /* noop */
  }
}

// ---------- CTA click (Agendar llamada) ----------
export function trackCTA(label: string) {
  trackEvent("CTA", "click", label, undefined, {
    link_url: "/demo",
  });
}

// ---------- WhatsApp click ----------
export function trackWhatsApp(label: string, destinationUrl?: string) {
  // GA4
  trackEvent("WhatsApp", "click", label, undefined, {
    link_url: destinationUrl || "",
    method: "whatsapp",
  });

  // Meta Pixel — Contact event
  trackPixelEvent("Contact", {
    method: "whatsapp",
    event_label: label,
    ...getUtmParams(),
  });
}

// ---------- Funnel step ----------
export function trackFunnelStep(stepNum: number, stepName?: string) {
  trackEvent(
    "Funnel",
    "step_complete",
    `Paso ${stepNum}${stepName ? ` - ${stepName}` : ""}`,
    stepNum,
    {
      funnel_step: stepNum,
      step_name: stepName || `step_${stepNum}`,
    },
  );
}

export function trackFunnelDescarte(reason: string) {
  trackEvent("Funnel", "descarte", reason, undefined, {
    descarte_reason: reason,
  });
}

// ---------- Lead ----------
export function trackLead(customEventId?: string) {
  const eventID = customEventId || generateEventId();

  // GA4 — generate_lead
  gtag("event", "generate_lead", {
    currency: "ARS",
    value: 390000,
    ...getUtmParams(),
  });

  // Meta Pixel — Lead
  trackPixelEvent("Lead", { currency: "USD" }, eventID);

  return eventID;
}

// ---------- Meta Pixel ----------
export function trackPixelEvent(
  eventName: string,
  params?: Record<string, unknown>,
  customEventId?: string,
) {
  try {
    if (typeof window !== "undefined" && (window as any).fbq) {
      const eventID = customEventId || generateEventId();
      (window as any).fbq("track", eventName, params || {}, { eventID });
    }
  } catch (error) {
    console.error("Pixel tracking error:", error);
  }
}

// ---------- Phone call click (tel:) ----------
export function trackPhoneCall(label: string) {
  trackEvent("Phone", "click", label, undefined, {
    method: "phone",
  });
  trackPixelEvent("Contact", { method: "phone", event_label: label });
}

// Re-export compat (viejos llamados directos a trackEvent/trackPixelEvent)
export default ReactGA;
